/**
 * AnalysisModeSelector - Visual mode selection tabs
 * Converted from TSX to JSX for college_recruiter_v1
 *
 * Features:
 * - Mode tabs with sport icons (baseball, bat, basketball, golf, soccer)
 * - Visual tab selector with active state indicator
 * - Mode hint text based on current selection
 *
 * @module v2/analyzer/AnalysisModeSelector
 */

import React from 'react';
import { ANALYSIS_MODES, getAllModes, getModesBySport } from '../../../lib/analysisModes';

/**
 * Baseball icon for pitch tipping (analyzing pitcher)
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const BaseballIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    {/* Baseball seam curves */}
    <path d="M12 2c-1.5 2-2 4-2 6s.5 4 2 6c1.5-2 2-4 2-6s-.5-4-2-6" />
    <path d="M12 22c1.5-2 2-4 2-6s-.5-4-2-6c-1.5 2-2 4-2 6s.5 4 2 6" />
    {/* Stitch marks */}
    <path d="M6.5 4.5l.5 1" />
    <path d="M5 7l.5 1" />
    <path d="M4.5 10l.5 1" />
    <path d="M17.5 4.5l-.5 1" />
    <path d="M19 7l-.5 1" />
    <path d="M19.5 10l-.5 1" />
    <path d="M6.5 19.5l.5-1" />
    <path d="M5 17l.5-1" />
    <path d="M4.5 14l.5-1" />
    <path d="M17.5 19.5l-.5-1" />
    <path d="M19 17l-.5-1" />
    <path d="M19.5 14l-.5-1" />
  </svg>
);

/**
 * Baseball bat icon for big league hitter (analyzing batter)
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const BatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Bat handle */}
    <path d="M4 20l2-2" />
    {/* Bat grip/knob */}
    <circle cx="3" cy="21" r="1.5" fill="currentColor" />
    {/* Bat body - tapered */}
    <path d="M6 18l12-12" strokeWidth="3" />
    <path d="M14 10l4-4" strokeWidth="4" />
    <path d="M16 8l3-3" strokeWidth="5" />
    {/* Bat barrel end */}
    <ellipse cx="20" cy="4" rx="2" ry="1.5" transform="rotate(-45 20 4)" fill="currentColor" />
  </svg>
);

/**
 * Golf icon for golf analysis (flag on green)
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const GolfIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="18" />
    <path d="M12 2l7 4-7 4" fill="currentColor" opacity="0.3" />
    <path d="M12 2l7 4-7 4" />
    <path d="M5 18c0 0 3-2 7-2s7 2 7 2" />
    <ellipse cx="12" cy="20" rx="8" ry="2" opacity="0.2" fill="currentColor" />
  </svg>
);

/**
 * Soccer icon for soccer analysis (soccer ball pentagon pattern)
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const SoccerIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="12,7 15,10.5 13.5,14 10.5,14 9,10.5" fill="currentColor" opacity="0.2" />
    <polygon points="12,7 15,10.5 13.5,14 10.5,14 9,10.5" />
    <line x1="12" y1="2" x2="12" y2="7" />
    <line x1="22" y1="12" x2="15" y2="10.5" />
    <line x1="19" y1="19" x2="13.5" y2="14" />
    <line x1="5" y1="19" x2="10.5" y2="14" />
    <line x1="2" y1="12" x2="9" y2="10.5" />
  </svg>
);

/**
 * Basketball icon for basketball analysis
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const BasketballIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10" />
    <path d="M12 2a15.3 15.3 0 0 0-4 10 15.3 15.3 0 0 0 4 10" />
    <path d="M2 12c0 0 3.5-3 10-3s10 3 10 3" transform="rotate(90 12 12)" opacity="0" /> {/* Hidden horizontal curve for now, keep simple */}
  </svg>
);

/**
 * ModeTab Component - Individual tab button
 * @param {Object} props
 * @param {Object} props.mode - Mode configuration object
 * @param {boolean} props.isActive - Whether this tab is active
 * @param {Function} props.onClick - Click handler
 * @param {boolean} [props.disabled=false] - Whether tab is disabled
 */
const ModeTab = ({ mode, isActive, onClick, disabled, compact = false }) => {
  let Icon = BaseballIcon;
  if (mode.icon === 'bat') Icon = BatIcon;
  if (mode.icon === 'basketball') Icon = BasketballIcon;
  if (mode.icon === 'golf') Icon = GolfIcon;
  if (mode.icon === 'soccer') Icon = SoccerIcon;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative flex flex-col items-center justify-center rounded-xl border-2 transition-all duration-200
        ${compact ? 'px-3 py-2 min-w-[140px]' : 'px-4 py-3 min-w-[160px] sm:min-w-[200px]'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isActive
          ? 'bg-primary/15 border-primary shadow-lg shadow-primary/20'
          : 'bg-muted/30 border-border hover:border-primary/50 hover:bg-muted/50'
        }
      `}
    >
      {/* Active indicator dot */}
      {isActive && (
        <div
          className="absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse"
          style={{ backgroundColor: mode.color }}
        />
      )}

      {/* Icon and title */}
      <div className="flex items-center gap-2 mb-1">
        <Icon
          className={`w-5 h-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
        />
        <span
          className={`font-semibold text-sm ${isActive ? 'text-primary' : 'text-foreground'}`}
        >
          {mode.shortName}
        </span>
      </div>

      {/* Description */}
      <p
        className={`text-[10px] text-center leading-tight max-w-[180px] ${
          isActive ? 'text-primary/80' : 'text-muted-foreground'
        }`}
      >
        {mode.description}
      </p>

      {/* Mode indicator line */}
      <div
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300 ${
          isActive ? 'w-3/4 opacity-100' : 'w-0 opacity-0'
        }`}
        style={{ backgroundColor: mode.color }}
      />
    </button>
  );
};

/**
 * Mode hint lookup — sport-aware descriptions for each analysis mode
 */
const MODE_HINTS = {
  'simple-mechanics': 'Quick mechanical breakdown of pitcher delivery',
  'pitch-tipping': 'Identify exploitable tells in pitcher mechanics',
  'big-league-hitter': 'Evaluate elite hitting via temporal-spatial markers',
  'basketball-analysis': 'Analyze shooting, footwork, and movement patterns',
  'basketball-biomechanics': 'Movement efficiency and injury prevention analysis',
  'basketball-shooting': 'Shot release, arc, footwork, and consistency eval',
  'basketball-team-defense': 'Defensive rotations, help-side, and scheme execution',
  'basketball-game-film': 'Full game tactical play-by-play breakdown',
  'deep-analysis-baseball': 'Comprehensive baseball analysis with PDF export',
  'deep-analysis-basketball': 'Comprehensive basketball analysis with PDF export',
  'golf-swing': 'Full swing breakdown using P-position framework (P1-P10)',
  'golf-biomechanics': 'Joint kinematics, ground forces, and injury prevention',
  'golf-kinematic-sequence': 'Pelvis-Thorax-Arm-Club energy transfer chain',
  'golf-short-game': 'Chipping, pitching, bunker play, and putting technique',
  'golf-course-management': 'Shot selection, risk-reward, and strategic decisions',
  'deep-analysis-golf': 'Comprehensive golf analysis with PDF export',
  'soccer-tactical': 'Formation, pitch control, pressing, and team shape analysis',
  'soccer-biomechanics': 'Kinematic load, fatigue, and injury risk assessment',
  'soccer-attacking': 'xG, EPV, chance creation, and attacking pattern analysis',
  'soccer-defensive': 'Pressing triggers, defensive block, and duel analysis',
  'soccer-set-pieces': 'Corner kicks, free kicks, and dead-ball tactical analysis',
  'deep-analysis-soccer': 'Comprehensive soccer analysis with PDF export',
};

/**
 * AnalysisModeSelector Component
 * @param {Object} props
 * @param {string} props.currentMode - Current mode ID
 * @param {Function} props.onModeChange - Mode change callback (mode) => void
 * @param {boolean} [props.disabled=false] - Whether selector is disabled
 * @param {string} [props.sportCategory] - Optional sport filter ('baseball', 'basketball', 'golf', or 'soccer')
 */
export const AnalysisModeSelector = ({
  currentMode,
  onModeChange,
  disabled = false,
  sportCategory = null,
  compact = false,
}) => {
  // Filter modes by sport if sportCategory is provided, otherwise show all
  const modes = sportCategory ? getModesBySport(sportCategory) : getAllModes();

  return (
    <div className="w-full">
      {/* Label */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Analysis Mode
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Tabs - 3 columns when compact, wrap otherwise */}
      <div className={compact
        ? "grid grid-cols-3 gap-2"
        : "flex flex-wrap justify-center gap-3"
      }>
        {modes.map((mode) => (
          <ModeTab
            key={mode.id}
            mode={mode}
            isActive={currentMode === mode.id}
            onClick={() => onModeChange(mode.id)}
            disabled={disabled}
            compact={compact}
          />
        ))}
      </div>

      {/* Current mode hint */}
      <div className={compact ? "mt-2 text-center" : "mt-3 text-center"}>
        <p className="text-xs text-muted-foreground">
          <span className="text-yellow-500 font-medium">
            {MODE_HINTS[currentMode] || ANALYSIS_MODES[currentMode]?.description || 'Analyzing performance'}
          </span>
        </p>
      </div>
    </div>
  );
};

export default AnalysisModeSelector;
